import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Trash2 } from "lucide-react";
import { useStore } from "../store/StoreContext";
import { img } from "../lib/format";
import EnquiryQuantity from "./EnquiryQuantity";
import CatalogueDetails from "./CatalogueDetails";
import useDialogFocus from "../lib/useDialogFocus";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cartDetailed, setQty, removeFromCart } = useStore();
  const navigate = useNavigate();
  const reduced = useReducedMotion();
  const dialogRef = useDialogFocus(cartOpen, () => setCartOpen(false));
  const go = (path) => { setCartOpen(false); navigate(path); };
  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div data-testid="cart-drawer-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-ink/40 backdrop-blur-[2px] z-[80]" onClick={() => setCartOpen(false)} />
          <motion.aside ref={dialogRef} tabIndex={-1} data-testid="cart-drawer" role="dialog" aria-modal="true" aria-label="Your enquiry selection" initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: reduced ? 0 : 0.35, ease: [0.22, 1, 0.36, 1] }} className="viewport-drawer fixed right-0 w-full max-w-[440px] bg-white z-[90] flex flex-col">
            <div className="flex items-center justify-between px-6 h-[76px] border-b border-line shrink-0">
              <h2 className="text-[22px] font-light">Your Enquiry</h2>
              <button data-testid="cart-drawer-close" onClick={() => setCartOpen(false)} aria-label="Close enquiry selection" className="flex min-h-11 min-w-11 items-center gap-2 p-2 -mr-2 hover:text-brass transition-colors duration-200">
                <span className="text-[11px] uppercase tracking-[0.3em]">Close</span><X className="w-5 h-5" strokeWidth={1.25} />
              </button>
            </div>
            {cartDetailed.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <p className="text-[18px] font-light text-charcoal">Your enquiry selection is currently empty.</p>
                <button onClick={() => go("/shop")} className="pill-dark mt-8 px-8">Discover the Collection</button>
                <button onClick={() => go("/catalogue")} className="mt-6 text-[12px] text-mocha underline underline-offset-4">Request the export catalogue</button>
              </div>
            ) : (
              <>
                <div data-lenis-prevent className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-6 space-y-6">
                  {cartDetailed.map(({ product, qty }) => (
                    <div key={product.slug} className="flex gap-4" data-testid={"cart-item-" + product.slug}>
                      <Link to={"/product/" + product.slug} onClick={() => setCartOpen(false)} className="w-20 h-24 bg-cream rounded-2xl shrink-0 overflow-hidden flex items-center justify-center">
                        <img src={img(product.images[0])} alt={product.name} className="w-full h-full object-contain p-2" />
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <Link to={"/product/" + product.slug} onClick={() => setCartOpen(false)} className="text-[15px] font-light leading-snug hover:text-brass transition-colors duration-200">{product.name}</Link>
                          <button onClick={() => removeFromCart(product.slug)} aria-label={"Remove " + product.name} className="text-mocha hover:text-ink transition-colors duration-200 p-2 -mr-2 h-fit min-w-11 min-h-11 flex justify-center items-center shrink-0"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                        </div>
                        <CatalogueDetails product={product} className="mt-1" />
                        <div className="mt-3"><EnquiryQuantity value={qty} onChange={(value) => setQty(product.slug, value)} id={"drawer-quantity-" + product.slug} compact /></div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-5 border-t border-line space-y-4 shrink-0">
                  <p className="text-[12px] text-mocha leading-relaxed">MOQ 500 pieces. Quantities are optional. Our team will confirm your requirements and export details.</p>
                  <button data-testid="cart-enquiry-button" onClick={() => go("/enquiry")} className="pill-dark w-full py-4 uppercase tracking-[0.2em] text-[11px]">Continue to enquiry</button>
                  <button onClick={() => go("/cart")} className="w-full min-h-11 text-center text-[11px] uppercase tracking-[0.2em] text-mocha hover:text-ink transition-colors duration-200 py-1">Review selection</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
